/**
 * Shipping Calculator Utility
 * Vypočíta orientačnú cenu dopravy na základe vzdialenosti
 */

import { getWorkerUrl } from './env';

interface Coordinates {
  lat: number;
  lng: number;
}

/**
 * Vypočíta vzdialenosť medzi dvoma bodmi pomocou Haversine formula
 * @param coord1 Súradnice používateľa
 * @param coord2 Súradnice inzerátu
 * @returns Vzdialenosť v kilometroch
 */
export const calculateDistance = (coord1: Coordinates, coord2: Coordinates): number => {
  const R = 6371; // Polomer Zeme v kilometroch
  const dLat = (coord2.lat - coord1.lat) * Math.PI / 180;
  const dLng = (coord2.lng - coord1.lng) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(coord1.lat * Math.PI / 180) * Math.cos(coord2.lat * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// In-memory geocode cache keyed by normalized address — repeated searches with
// the same location (typical when tweaking other filters) hit Google only once
// per session. Failures (null) are not cached so a transient error can recover.
const geocodeCache = new Map<string, { coords: Coordinates; timestamp: number }>();
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hodin

/**
 * Získa súradnice z adresy cez serverový geocoding proxy endpoint
 * @param address Adresa (mesto, PSČ)
 * @returns Súradnice alebo null
 */
export const geocodeAddress = async (address: string): Promise<Coordinates | null> => {
  const cacheKey = address.trim().toLowerCase();
  const cached = geocodeCache.get(cacheKey);
  const now = Date.now();

  if (cached) {
    if (now - cached.timestamp < CACHE_TTL_MS) {
      return cached.coords;
    } else {
      geocodeCache.delete(cacheKey);
    }
  }

  try {
    // Deliberately NOT VITE_GOOGLE_MAPS_API_KEY. That key ships to the browser
    // and therefore carries an HTTP-referrer restriction, and Google refuses
    // referrer-restricted keys on the Geocoding web service outright — measured
    // with a legitimate espacero.com referrer, it answers REQUEST_DENIED every
    // time. This used to return null for every address, silently, which is why
    // radius search never found a neighbouring town.
    //
    // The worker holds a server key instead, rate-limits the endpoint and
    // caches results, so one town costs one Google call ever.
    const response = await fetch(
      `${getWorkerUrl('D1')}/api/geo/search?q=${encodeURIComponent(address + ', Slovensko')}`
    );
    if (!response.ok) {
      throw new Error(`Geocoding request failed with status ${response.status}`);
    }

    // The endpoint answers with a bare array on success and an object on
    // failure, and hands lat/lon back as strings.
    const results = await response.json();
    const firstResult = Array.isArray(results) ? results[0] : null;
    const lat = Number(firstResult?.lat);
    const lng = Number(firstResult?.lon);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      const coords = { lat, lng };
      geocodeCache.set(cacheKey, { coords, timestamp: now });
      return coords;
    }
    return null;
  } catch (error) {
    console.error('Chyba pri geocoding:', error);
    return null;
  }
};

/**
 * Získa aktuálnu polohu používateľa
 * @returns Súradnice alebo null
 */
export const getUserLocation = (): Promise<Coordinates | null> => {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
      },
      (error) => {
        console.error('Chyba pri získavaní polohy:', error);
        resolve(null);
      },
      {
        enableHighAccuracy: false,
        timeout: 5000,
        maximumAge: 300000 // 5 minút cache
      }
    );
  });
};

/**
 * Vypočíta orientačnú cenu dopravy
 * @param distance Vzdialenosť v kilometroch
 * @returns Cena dopravy v EUR
 */
export const calculateShippingCost = (distance: number): number => {
  // Základná cena: 5 EUR
  // + 0.5 EUR za každý kilometer
  // Minimum: 5 EUR, Maximum: 50 EUR
  const baseCost = 5;
  const perKm = 0.5;
  const cost = baseCost + (distance * perKm);
  return Math.min(Math.max(cost, 5), 50);
};

/**
 * Vypočíta cenu dopravy medzi používateľom a inzerátom
 * @param listingLocation Lokácia inzerátu (mesto, PSČ)
 * @returns Cena dopravy alebo null ak sa nedá vypočítať
 */
export const calculateShippingForListing = async (listingLocation: string): Promise<number | null> => {
  try {
    // Získať polohu používateľa
    const userCoords = await getUserLocation();
    if (!userCoords) {
      return null;
    }

    // Získať súradnice inzerátu
    const listingCoords = await geocodeAddress(listingLocation);
    if (!listingCoords) {
      return null;
    }

    // Vypočítať vzdialenosť
    const distance = calculateDistance(userCoords, listingCoords);

    // Vypočítať cenu dopravy
    return calculateShippingCost(distance);
  } catch (error) {
    console.error('Chyba pri výpočte dopravy:', error);
    return null;
  }
};
